import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Card from '../components/card/Card';
import H from '../components/texts/h/H';
import P from '../components/texts/p/P';
import Button from '../components/button/Button';
import Icon from '../components/icon/Icon';
import DropDown from '../components/dropdown/DropDown';
import Table from '../components/table/table';
import TableSkeleton from '../components/TableSkeleton/TableSkeleton';
import { getGuaranteeDetail } from '../services/GuaranteesService';
import { routes } from '../routes/routePaths';
import { useClientTheme } from '../context/ThemeContext';

interface ProductLineSummary {
  product_line_code: string;
  product_line_label: string;
  total_amount_ars: number;
  total_amount_usd: number;
}

interface ProductLine {
  application_product_line_id: number;
  product_line_code: string;
  product_line_label: string;
  id_currency: number;
  currency_label: string;
  approved_amount: number;
  monetization_amount: number | null;
  available_amount: number;
}

interface ApprovedApplication {
  application_number: number;
  product_lines: ProductLine[];
}

interface GuaranteeDetailData {
  cuit: string;
  company_name: string;
  total_approved_amount_ars: number;
  total_approved_amount_usd: number;
  approved_applications_count: number;
  total_monetization_amount_ars: number;
  total_monetization_amount_usd: number;
  available_amount_ars: number;
  available_amount_usd: number;
  product_line_summary: ProductLineSummary[];
  approved_applications: ApprovedApplication[];
}

const GuaranteesDetail = () => {
  const { cuit } = useParams<{ cuit: string }>();
  const navigate = useNavigate();
  const { clientTheme } = useClientTheme();
  const [guaranteeData, setGuaranteeData] = useState<GuaranteeDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [balanceFilter, setBalanceFilter] = useState('Todas las líneas');
  const [sortOrder, setSortOrder] = useState('Más recientes');

  useEffect(() => {
    if (cuit) {
      fetchGuaranteeDetail();
    }
  }, [cuit]);

  const fetchGuaranteeDetail = async () => {
    if (!cuit) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await getGuaranteeDetail(cuit);
      if (response.success && 'data' in response) {
        setGuaranteeData(response.data);
      } else {
        setError('Error al cargar los datos del aval');
        navigate(routes.guarantees);
      }
    } catch (err) {
      setError('Error al cargar los datos del aval');
      navigate(routes.guarantees);
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate(routes.guarantees);
  };

  const handleRowClicked = (row: any) => {
    // Only navigate if it's not a total row and has the required data
    if (row.product_line_label !== 'Total' && row.application_product_line_id) {
      navigate(`/avales/${cuit}/linea/${row.application_product_line_id}`);
    }
  };

  const formatNumber = (amount: number) => {
    // Si es un número entero, no mostrar decimales
    if (amount % 1 === 0) {
      return amount.toLocaleString('es-AR');
    }
    // Si tiene decimales, usar formato argentino (coma como separador decimal)
    return amount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const renderSummaryCard = (title: string, arsAmount: number, usdAmount: number, hasEditIcon = false, iconName = 'edit_outline', iconSize = 24) => {
    const isDisabled = arsAmount === 0 && usdAmount === 0;
    
    return (
      <Card variant='neutral' modifier='regular' spacing={'4'}>
        <Card display='flex' justifyContent='space-between' alignItems='center'>
          <H variant="secondary-2" withMargin={false} type='4' weight='bold' modifier={isDisabled ? 'weak' : 'strong'}>
            {title}
          </H>
          {hasEditIcon && (
            <Icon 
              name={iconName}
              variant='secondary-2'
              modifier={isDisabled ? 'weak' : 'regular'}
              width={iconSize}
              height={iconSize}
            />
          )}
        </Card>
        <Card margin={{ top: '2' }}>
          <H variant='secondary-2' type="2" weight="bold" modifier={isDisabled ? 'weak' : 'regular'} withMargin={false}>
            ARS {formatNumber(arsAmount)}
          </H>
          <H variant='secondary-2' type="2" weight="bold" modifier={isDisabled ? 'weak' : 'regular'} withMargin={false}>
            USD {formatNumber(usdAmount)}
          </H>
        </Card>
      </Card>
    );
  };

  const getProductLineSummary = (code: string): ProductLineSummary | undefined => {
    return guaranteeData?.product_line_summary.find(pl => pl.product_line_code === code);
  };

  const filterAndSortApplications = () => {
    if (!guaranteeData) return [];

    let filteredApplications = guaranteeData.approved_applications;

    // Filter by balance availability
    if (balanceFilter === 'Con saldo disponible') {
      filteredApplications = filteredApplications.filter(app => 
        app.product_lines.some(pl => pl.available_amount > 0)
      );
    } else if (balanceFilter === 'Sin saldo disponible') {
      filteredApplications = filteredApplications.filter(app => 
        app.product_lines.every(pl => pl.available_amount === 0)
      );
    }

    // Sort applications
    filteredApplications.sort((a, b) => {
      switch (sortOrder) {
        case 'Menos recientes':
          return a.application_number - b.application_number;
        case 'Más disponible':
          const aTotal = a.product_lines.reduce((sum, pl) => sum + pl.available_amount, 0);
          const bTotal = b.product_lines.reduce((sum, pl) => sum + pl.available_amount, 0);
          return bTotal - aTotal;
        case 'Menos disponible':
          const aTotalLeast = a.product_lines.reduce((sum, pl) => sum + pl.available_amount, 0);
          const bTotalLeast = b.product_lines.reduce((sum, pl) => sum + pl.available_amount, 0);
          return aTotalLeast - bTotalLeast;
        case 'Más recientes':
        default:
          return b.application_number - a.application_number;
      }
    });

    return filteredApplications;
  };

  const renderApplicationTable = (application: ApprovedApplication) => {
    const columns = [
      { 
        name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Instrumento</P>, 
        selector: (row: any) => (
          <P variant='secondary-2' type="3" weight={row.product_line_label === 'Total' ? 'bold' : 'regular'} withMargin={false}>
            {row.product_line_label}
          </P>
        ),
        grow: 1
      },
      { 
        name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Divisa</P>, 
        selector: (row: any) => (
          <P variant='secondary-2' type="3" weight={row.product_line_label === 'Total' ? 'bold' : 'regular'} withMargin={false}>
            {row.currency_display}
          </P>
        ),
        grow: 0.8
      },
      { 
        name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Total</P>, 
        selector: (row: any) => (
          <P variant='secondary-2' type="3" weight={row.product_line_label === 'Total' ? 'bold' : 'regular'} withMargin={false}>
            {row.approved_amount_display}
          </P>
        ),
        grow: 0.8
      },
      { 
        name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Utilizado</P>, 
        selector: (row: any) => (
          <P variant='secondary-2' type="3" weight={row.product_line_label === 'Total' ? 'bold' : 'regular'} withMargin={false}>
            {row.monetization_amount_display}
          </P>
        ),
        grow: 0.8
      },
      { 
        name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Disponible</P>, 
        selector: (row: any) => (
          <P variant='secondary-2' type="3" weight={row.product_line_label === 'Total' ? 'bold' : 'regular'} withMargin={false}>
            {row.available_amount_display}
          </P>
        ),
        grow: 0.8
      },
      { 
        name: '', 
        selector: (row: any) => (
          row.product_line_label !== 'Total' ? (
            <Icon 
              name='view_outline'
              variant='info'
              modifier='regular'
              width={24}
              height={24}
              cursor='pointer'
              onClick={() => handleRowClicked(row)}
            />
          ) : null
        ),
        grow: 0.4
      }
    ];

    // Prepare data with display values
    const tableData = application.product_lines.map(pl => ({
      ...pl,
      currency_display: pl.id_currency === 1 ? 'Pesos argentinos' : 'Dolares',
      approved_amount_display: formatNumber(pl.approved_amount),
      monetization_amount_display: pl.monetization_amount 
        ? formatNumber(pl.monetization_amount)
        : '-',
      available_amount_display: formatNumber(pl.available_amount)
    }));

    // Calculate totals
    const arsTotal = {
      approved: application.product_lines.filter(pl => pl.id_currency === 1).reduce((sum, pl) => sum + pl.approved_amount, 0),
      monetization: application.product_lines.filter(pl => pl.id_currency === 1).reduce((sum, pl) => sum + (pl.monetization_amount || 0), 0),
      available: application.product_lines.filter(pl => pl.id_currency === 1).reduce((sum, pl) => sum + pl.available_amount, 0)
    };

    const usdTotal = {
      approved: application.product_lines.filter(pl => pl.id_currency === 2).reduce((sum, pl) => sum + pl.approved_amount, 0),
      monetization: application.product_lines.filter(pl => pl.id_currency === 2).reduce((sum, pl) => sum + (pl.monetization_amount || 0), 0),
      available: application.product_lines.filter(pl => pl.id_currency === 2).reduce((sum, pl) => sum + pl.available_amount, 0)
    };

    // Add total rows
    const totalRowsData = [
      {
        product_line_label: 'Total',
        currency_display: 'Pesos Argentinos',
        approved_amount_display: formatNumber(arsTotal.approved),
        monetization_amount_display: formatNumber(arsTotal.monetization),
        available_amount_display: formatNumber(arsTotal.available)
      },
      {
        product_line_label: 'Total',
        currency_display: 'Dolares',
        approved_amount_display: formatNumber(usdTotal.approved),
        monetization_amount_display: formatNumber(usdTotal.monetization),
        available_amount_display: formatNumber(usdTotal.available)
      }
    ];

    const finalData = [...tableData, ...totalRowsData];

    return (
      <Card margin={{ top: '3' }}>
        <Card margin={{ bottom: '3' }} variant={clientTheme ? 'primary' : 'success'} modifier='weak' spacing={'2'} display='flex' justifyContent='space-between'>
          <H withMargin={false} variant="secondary-2" type='3' weight='bold' modifier='strong'>
            Linea {application.application_number}
          </H>
        </Card>
        <Table
          columns={columns}
          data={finalData}
          tableData={{ page: 0, size: finalData.length, totalElements: finalData.length, totalPages: 1 }}
          handleChangePage={() => {}}
          onRowClicked={handleRowClicked}
        />
      </Card>
    );
  };

  const balanceFilterItems = [
    { label: 'Todas las líneas' },
    { label: 'Con saldo disponible' },
    { label: 'Sin saldo disponible' }
  ];

  const sortOrderItems = [
    { label: 'Más recientes' },
    { label: 'Menos recientes' },
    { label: 'Más saldo disponible' },
    { label: 'Menos saldo disponible' }
  ];

  if (loading) {
    return (
      <Card variant='neutral' modifier='inverted' spacing={'4'} height='-webkit-fill-available' width='85vw'>
        <Card margin={{ top: '4' }}>
          <TableSkeleton rows={8} columns={4} />
        </Card>
      </Card>
    );
  }

  if (error || !guaranteeData) {
    return (
      <Card variant='neutral' modifier='inverted' spacing={'4'} height='-webkit-fill-available' width='85vw'>
        <Card display='flex' justifyContent='center' alignItems='center' height='300px'>
          <P type="1" weight="regular" modifier="weak">{error || 'Aval no encontrado'}</P>
        </Card>
      </Card>
    );
  }

  return (
    <Card variant='neutral' modifier='inverted' spacing={'4'} height='-webkit-fill-available' width='85vw'>
      {/* Header with back button and title */}
      <Card display='flex' alignItems='center' gap='16px' margin={{ bottom: '4' }}>
        <Button 
          icon='arrow_left_outline'
          variant='tertiary'
          size='sm'
          iconAlign='center'
          onClick={handleGoBack}
          aria-label="Volver a gestión de avales"
        />
        <Card display='flex' alignItems='baseline' gap='8px'>
          <H withMargin={false} variant="secondary-2" type='2' weight='bold' modifier='strong'>
            Gestión de avales {guaranteeData.company_name}
          </H>
          <H variant="secondary-2" type="3" weight="bold" modifier="strong">
            {guaranteeData.cuit}
          </H>
        </Card>
      </Card>

      {/* Summary Cards - Top Row (3 cards) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
        {renderSummaryCard(
          'Total',
          guaranteeData.total_approved_amount_ars,
          guaranteeData.total_approved_amount_usd
        )}
        {renderSummaryCard(
          'Utilizado',
          guaranteeData.total_monetization_amount_ars,
          guaranteeData.total_monetization_amount_usd
        )}
        {renderSummaryCard(
          'Disponible',
          guaranteeData.available_amount_ars,
          guaranteeData.available_amount_usd
        )}
      </div>

      {/* Summary Cards - Bottom Row (5 cards) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '16px', marginBottom: '48px' }}>
        {renderSummaryCard(
          'CDP Propios',
          getProductLineSummary('001')?.total_amount_ars || 0,
          getProductLineSummary('001')?.total_amount_usd || 0,
          true,
          'group'
        )}
        {renderSummaryCard(
          'CDP Terceros',
          getProductLineSummary('002')?.total_amount_ars || 0,
          getProductLineSummary('002')?.total_amount_usd || 0,
          true,
          'group'
        )}
        {renderSummaryCard(
          'Prestamos',
          getProductLineSummary('003')?.total_amount_ars || 0,
          getProductLineSummary('003')?.total_amount_usd || 0,
          true,
          'bank'
        )}
        {renderSummaryCard(
          'Pagare Dolar Link',
          getProductLineSummary('006')?.total_amount_ars || 0,
          getProductLineSummary('006')?.total_amount_usd || 0,
          true,
          'moneyDollarBox'
        )}
        {renderSummaryCard(
          'Pagare en Pesos',
          getProductLineSummary('007')?.total_amount_ars || 0,
          getProductLineSummary('007')?.total_amount_usd || 0,
          true,
          'moneyPesosBox',
          22
        )}
      </div>

      {/* Credit Lines Section */}
      <Card margin={{ bottom: '4' }}>
        <Card margin={{ bottom: '3' }}>
          <H withMargin={false} type='2' weight='bold' modifier='strong'>
            Líneas de créditos generales activas
          </H>
        </Card>
        
        {/* Filters */}
        <Card display='flex' gap='16px' margin={{ bottom: '3' }}>
          <Card width='200px'>
            <Card margin={{ bottom: '2' }}>
              <P variant='secondary-2' type="4" weight="regular" withMargin={false}>
                Mostrar líneas
              </P>
            </Card>
            <DropDown
              id="balance-filter"
              items={balanceFilterItems}
              title={balanceFilter}
              onChange={(value: string) => setBalanceFilter(value)}
            />
          </Card>
          <Card width='200px'>
            <Card margin={{ bottom: '2' }}>
              <P variant='secondary-2' type="4" weight="regular" withMargin={false}>
                Orden
              </P>
            </Card>
            <DropDown
              id="sort-order"
              items={sortOrderItems}
              title={sortOrder}
              onChange={(value: string) => setSortOrder(value)}
            />
          </Card>
        </Card>
      </Card>

      {/* Application Tables */}
      {filterAndSortApplications().map((application) => (
        <div key={application.application_number}>
          {renderApplicationTable(application)}
        </div>
      ))}
    </Card>
  );
};

export default GuaranteesDetail;